import React from 'react';
import { Box, Button, Chip, IconButton, MenuItem, Select, Typography, Avatar, Tooltip } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

// Belge türleri (DOSYA_TIPI)
export const DOSYA_TIPLERI = [
  { value: 'FOTO', label: 'Fotoğraf' },
  { value: 'KALIBRASYON', label: 'Kalibrasyon belgesi' },
  { value: 'ETIKET', label: 'Etiket' },
  { value: 'FATURA', label: 'Fatura' },
  { value: 'DIGER', label: 'Diğer' }
];
const tipLabel = t => (DOSYA_TIPLERI.find(x => x.value === t) || DOSYA_TIPLERI[4]).label;
const isImage = ct => /^image\//i.test(String(ct || ''));

// Yeni eklenen belgeler arasında dosya tipi seçilmemiş olan var mı? (zorunlu tip kontrolü)
export const tipEksikVar = (yeni = []) => (yeni || []).some(d => !d.tip);

/*
  Ortak çoklu belge yükleyici.
  Props:
    mevcut       : [{ Id, DosyaAdi, ContentType, Tip }]        (DB'deki kayıtlı belgeler)
    onMevcutSil  : (Id) => void                                 (kayıtlıyı sil işaretle)
    onGoster     : ('mevcut'|'yeni', item) => void              (belgeyi büyük göster/aç)
    yeni         : [{ uid, ad, contentType, tip, base64, url }] (bu oturumda eklenenler)
    setYeni      : (arr) => void
    hint         : üstteki statik açıklama metni (opsiyonel)
*/
export default function DosyaYukleyici({ mevcut = [], onMevcutSil, onGoster, yeni = [], setYeni, hint, kapakYeniUid, onKapakSec, kapakAktif = true, tipZorunlu = false }) {
  const kapakBtn = (kaynak, item, secili) => (
    <Tooltip key={'k' + kaynak + (item.uid || item.Id)} title={secili ? 'Ana görsel (kapak)' : 'Kapak yap'}>
      <IconButton size="small" onClick={() => onKapakSec && onKapakSec(kaynak, item)}>
        {secili ? <StarIcon fontSize="small" style={{ color: '#f9a825' }} /> : <StarBorderIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
  const onSelect = e => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    Promise.all(files.map(f => new Promise(res => {
      const r = new FileReader();
      r.onload = ev => {
        const url = String(ev.target.result || '');
        const base64 = url.includes(',') ? url.split(',')[1] : url;
        res({
          uid: `${Date.now()}_${Math.round(performance.now())}_${f.name}`,
          ad: f.name, contentType: f.type || 'application/octet-stream',
          tip: tipZorunlu ? '' : (isImage(f.type) ? 'FOTO' : 'DIGER'),
          base64, url
        });
      };
      r.readAsDataURL(f);
    }))).then(items => setYeni([...(yeni || []), ...items]));
    e.target.value = '';
  };

  const setTip = (uid, tip) => setYeni((yeni || []).map(d => d.uid === uid ? { ...d, tip } : d));
  const silYeni = uid => setYeni((yeni || []).filter(d => d.uid !== uid));

  const kart = (children, key) => (
    <Box key={key} sx={{
      display: 'flex', alignItems: 'center', gap: 1, p: 1,
      border: '1px solid', borderColor: 'divider', borderRadius: '8px', backgroundColor: '#fafafa'
    }}>{children}</Box>
  );

  const thumb = (img, ct, onClick) => (
    img
      ? <Avatar variant="rounded" src={img} onClick={onClick}
          sx={{ width: 44, height: 44, cursor: onClick ? 'pointer' : 'default' }} />
      : <Avatar variant="rounded" onClick={onClick}
          sx={{ width: 44, height: 44, bgcolor: 'grey.200', color: 'grey.600', cursor: onClick ? 'pointer' : 'default' }}>
          <InsertDriveFileIcon fontSize="small" />
        </Avatar>
  );

  return (
    <Box>
      {hint && (
        <Box sx={{
          display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1, p: 1,
          borderRadius: '8px', backgroundColor: '#e8f0fe', color: '#1a4b8c', fontSize: 12.5
        }}>
          <AttachFileIcon fontSize="small" />
          <span>{hint}</span>
        </Box>
      )}

      <Button variant="outlined" component="label" color="secondary" startIcon={<PhotoCameraIcon />} size="small">
        Dosya Seç (çoklu)
        <input type="file" hidden multiple onChange={onSelect} />
      </Button>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1, maxHeight: 300, overflowY: 'auto', overflowX: 'hidden' }}>
        {/* Kayıtlı belgeler */}
        {mevcut.map(m => kart([
          thumb(null, m.ContentType, () => onGoster && onGoster('mevcut', m)),
          <Box key="i" sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {m.DosyaAdi || 'Belge'}
            </Box>
            <Chip size="small" label={tipLabel(m.Tip)} sx={{ height: 18, mt: 0.25 }} />
            {m.Kapak && <Chip size="small" label="kapak" sx={{ height: 18, mt: 0.25, ml: 0.5, bgcolor: '#fff8e1', color: '#f57f17' }} />}
          </Box>,
          kapakAktif && isImage(m.ContentType) ? kapakBtn('mevcut', m, !!m.Kapak) : null,
          <Tooltip title="Göster" key="g"><IconButton size="small" onClick={() => onGoster && onGoster('mevcut', m)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>,
          <Tooltip title="Sil" key="d"><IconButton size="small" onClick={() => onMevcutSil && onMevcutSil(m.Id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
        ], 'm' + m.Id))}

        {/* Bu oturumda eklenenler */}
        {(yeni || []).map(d => kart([
          thumb(isImage(d.contentType) ? d.url : null, d.contentType, () => onGoster && onGoster('yeni', d)),
          <Box key="i" sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {d.ad}
            </Box>
            <Select size="small" variant="standard" value={d.tip} displayEmpty
              error={tipZorunlu && !d.tip}
              onChange={e => setTip(d.uid, e.target.value)}
              sx={{ fontSize: 12, mt: 0.25, ...(tipZorunlu && !d.tip ? { color: '#d32f2f' } : {}) }}>
              {tipZorunlu && <MenuItem value=""><em>Tür seçin…</em></MenuItem>}
              {DOSYA_TIPLERI.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </Select>
          </Box>,
          kapakAktif && isImage(d.contentType) ? kapakBtn('yeni', d, d.uid === kapakYeniUid) : null,
          <Chip key="new" size="small" color="secondary" label="yeni" sx={{ height: 18 }} />,
          <Tooltip title="Kaldır" key="d"><IconButton size="small" onClick={() => silYeni(d.uid)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
        ], d.uid))}
      </Box>

      {mevcut.length === 0 && (yeni || []).length === 0 && (
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
          Henüz belge eklenmedi.
        </Typography>
      )}
    </Box>
  );
}
